import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import {
  useLoaderData,
  useNavigate,
  useParams,
  useBlocker,
} from "react-router-dom";
import styled from "@emotion/styled";

import {
  annotateScreenshot,
  arrayToCsv,
  b64toBlob,
  downloadAndUploadToStorage,
  downloadBlob,
  generateEnrichedPrompts,
  getDigitsAfterFirstSemicolon,
  getErrorMessage,
  getPromptStepsFromTipTapNode,
  getPromptStepsText,
  getInputValues,
  parseMarkdownTemplate,
  replaceItemFomArray,
  replaceTemplateItem,
  trackAmplitudeEvent,
  trackSentryException,
  uploadScreenshotToStorage,
  shouldUseAppetizeClone,
} from "@/utils/helpers";
import { markdownModeAtom, testEditorPropertiesAtom } from "@/atoms/test-editor-atom";
import { customFirestore, customStorage } from "@/firebase";
import { currentActor } from "@/utils/auditMeta";
import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  runTransaction,
  setDoc,
  Timestamp,
  updateDoc,
} from "firebase/firestore";
import useExecuteSteps, {
  type ExecutionStepState,
} from "../../routes/test-run/use-execute-steps";
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
  uploadString,
} from "firebase/storage";
import {
  getNestedDependencies,
  getRuns,
} from "@/atoms/test-nested-dependecies-atom";
import { customAlertStateAtom } from "../custom-alert";
import {
  useMutation,
  useMutationState,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import TestEditorSettings from "./testEditorSettings";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Tab,
  Tabs,
  ThemeProvider,
  Tooltip,
} from "@mui/material";
import {
  Compare,
  Notes,
  Save,
  Settings,
  Terminal,
  Wifi,
} from "@mui/icons-material";
import Typography from "@mui/material/Typography";
import TipTapEditor from "./tip-tap-editor/tipTapEditor";
import { testEditorAssistantAtom } from "./testEditorAssistant";
import { appetizeSessionStore, buildLogsAtomWithId } from "@/atoms";
import {
  type BuildData,
  type CachedScreenData,
  type CompanyData,
  type DependencyFormState,
  type DependencyInputValues,
  type LoaderApp,
  type PromptStep,
  type RunExecutionData,
  type SessionConfigData,
  type Test,
  type TestFolder,
  type TestSettings,
  type MediaFile,
} from "@/utils/types";
import { AuthContext } from "@/auth/AuthContext";
import { deleteCachedSteps } from "@/utils/firebase_operations";
import { RenderSpinner } from "../settings_components";
import { TiptapCollabProvider } from "@tiptap-pro/provider";
import * as Y from "yjs";
import TestEditorExecutionLogs from "./execution-logs/testEditorExecutionLogs";
import DeviceLogs from "./device-logs/DeviceLogs";
import NetworkLogs from "./network-logs/NetworkLogs";
import { testBuilderCustomAlertStateAtom } from "@/routes/test-builder-page";
import { AsyncConfirmationModal } from "../asyncConfirmationModal";
import { Form, Formik, type FormikProps } from "formik";
import FormikTextInput from "../../formikComponents/formik-text-input";
import { array, object, string, ValidationError } from "yup";
import { darkTheme, lightTheme } from "@/themes";
import { forceUpdateManualConditionsStateAtom } from "@/routes/force-update-dialog";
import FormikSelect from "../../formikComponents/formik-select";

import { getTestsAtom } from "@/atoms/testing-settings-atoms";
import dependenciesForTestAtom from "../../atoms/tests-atom";
import { kAllTestsId } from "@/routes/tests-home/tests-tree-view";
import { useStore } from "@tanstack/react-store";
import MarkdownImportDialog from "../markdown-test-editor/import-dialog";
import {
  docHasRequiredMarkdownHeadings,
  markdownToHtml,
  tiptapJsonToMarkdown,
} from "../markdown-test-editor/markdown-conversion";
import LlmValidationDialog, {
  fetchTestContentValidation,
  type ValidationIssue,
} from "../markdown-test-editor/llm-validation-dialog";
import { mainTipTapEditorAtom } from "./tip-tap-editor/tipTapEditor";
import apiClient from "@/utils/api-client";
import LoadingButton from "@mui/lab/LoadingButton";

const succeedingStep: PromptStep = {
  text: "Succeed the test once you finish the above step(s).",
  plainText: "Succeed the test once you finish the above step(s).",
};

interface CreateInteractionLogsParams {
  newEnrichedInteractions: any[];
  newPromptGenerationScreenshots: any[];
  logId: number;
  lastScreenshot: string;
  lastEditTexts: any[];
}

export type { CreateInteractionLogsParams };

export default function TestEditorGpt({
  test,
  tipTapJWTData,
  buildData,
  companyData,
  sessionConfig,
  markdownMode = false,
}: {
  test?: Test;
  tipTapJWTData: { jwt: string };
  buildData: BuildData;
  companyData: CompanyData;
  sessionConfig: SessionConfigData;
  markdownMode?: boolean;
}) {
  const session = useStore(appetizeSessionStore, (state) => state);

  const usingAppetizeClone = shouldUseAppetizeClone({
    settingsData: companyData.settings,
    buildData,
    fromEditor: true,
  });

  const mutationData = useMutationState({
    filters: { mutationKey: ["createSessionMutation"], status: "success" },
    select: (mutation) => mutation.state.data,
  });

  const appetizeCloneSession = mutationData.at(-1);

  const [isEditing, setIsEditing] = useState(false);
  const [isExecutingFullTest, setIsExecutingFullTest] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const mainTipTapEditor = useAtomValue(mainTipTapEditorAtom);
  const setMarkdownMode = useSetAtom(markdownModeAtom);

  type PreflightChoice = "optimize" | "just-run" | "cancel";
  const [preflightDialog, setPreflightDialog] = useState<{
    open: boolean;
    optimizing: boolean;
    resolve: ((choice: PreflightChoice) => void) | null;
  }>({ open: false, optimizing: false, resolve: null });

  type ValidationChoice = "proceed" | "cancel";
  const [validationDialog, setValidationDialog] = useState<{
    open: boolean;
    markdown: string;
    issues: ValidationIssue[];
    resolve:
      | ((result: {
          choice: ValidationChoice;
          updatedMarkdown: string;
          markdownChanged: boolean;
        }) => void)
      | null;
  }>({ open: false, markdown: "", issues: [], resolve: null });
  const [validatingTestContent, setValidatingTestContent] = useState(false);

  useEffect(() => {
    setMarkdownMode(markdownMode);
    return () => {
      setMarkdownMode(false);
    };
  }, [markdownMode, setMarkdownMode]);
  const [testEditorAssistantState, setTestEditorAssistantState] = useAtom(
    testEditorAssistantAtom(buildData.id)
  );

  useEffect(() => {
    if (test != null) {
      const newPromptGenerationScreenshots = test.promptGenerationScreenshots!;
      const newEnrichedInteractions = test.enrichedInteractionsLogs!;
      const newEnrichedFromApiInteractions = test.enrichedInteractionsLogs!;

      setTestEditorAssistantState({
        promptGenerationScreenshots: newPromptGenerationScreenshots,
        enrichedInteractions: newEnrichedInteractions,
        enrichedFromApiInteractions: newEnrichedFromApiInteractions,
      });
    }
  }, [test]);

  const firstPressedKeyTimestamp = useRef(0);
  const [localLastSuccessfulRun, setLocalLastSuccessfulRun] = useState<
    CachedScreenData[]
  >([]);

  const [auxLocalLastSuccessfulRun, setAuxLocalLastSuccessfulRun] = useState<
    CachedScreenData[] | null
  >(null);
  // One-shot guard for the post-success cleanup. We used to gate the cleanup
  // off `auxLocalLastSuccessfulRun == null`, but aux is now snapshotted at run
  // start (so the displayed baseline reflects the pre-run state, not the
  // newly-written one) — so we need an independent flag to make sure cleanup
  // still runs exactly once per success.
  const successCleanupDoneRef = useRef(false);

  const setTestBuilderCustomAlertState = useSetAtom(
    testBuilderCustomAlertStateAtom
  );

  const setForceUpdateManualConditionsState = useSetAtom(
    forceUpdateManualConditionsStateAtom
  );

  const [pressedKeys, setPressedKeys] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState("prompt");
  const [auxDisplayPasteStep, setAuxDisplayPasteStep] = useState(false);

  const { user } = useContext<any>(AuthContext);
  const { buildId } = useParams();

  const loaderData = useLoaderData() as LoaderApp;
  const { interactionLogs } = useAtomValue(loaderData.buildLogsData);

  const dependenciesForTest = useAtomValue(
    dependenciesForTestAtom({
      organizationId: buildData.organisationId,
      testId: test?.id,
    })
  );
  const scrollableRef = useRef(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [testEditorProperties, setTestEditorProperties] = useAtom(
    testEditorPropertiesAtom
  );

  const [selectedDependencyId, setSelectedDependencyId] = useState(
    test?.dependencies?.at(0)?.testId ?? null
  );
  const dependencyFormRef = useRef<FormikProps<DependencyFormState>>(null);
  const dependencyFormInitialState = useMemo(() => {
    const dependencyFormState = dependencyFormRef.current;

    if (
      dependencyFormState != null &&
      dependencyFormState?.values?.dependencyId == null
    ) {
      return { selectedDependencyId, dependencyInputs: [] };
    }

    const alreadySavedDependencyInputs: DependencyInputValues =
      test?.dependencyInputValues ?? {};

    const currentDependencyId =
      dependencyFormState?.values?.dependencyId ?? selectedDependencyId;

    const dependencyTest = dependenciesForTest?.data?.find(
      (test) => test.id === currentDependencyId
    );

    return {
      selectedDependencyId,
      dependencyInputs:
        dependencyTest?.settings?.inputKeys
          ?.filter((inputKey) => inputKey.key != null)
          .map((inputKey) => {
            return {
              key: inputKey.key,
              value:
                alreadySavedDependencyInputs?.[currentDependencyId!]?.find(
                  (savedInput) => savedInput.key === inputKey.key
                )?.value ?? "",
            };
          }) ?? [],
    };
  }, [test, dependenciesForTest, selectedDependencyId]);

  const promptSteps = useMemo(
    () =>
      getPromptStepsFromTipTapNode({
        node: testEditorProperties.testCommands,
      }),
    [testEditorProperties.testCommands]
  );

  const setCustomAlertState = useSetAtom(customAlertStateAtom);

  const handleChangeTab = (event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };

  const [testCommandsUpdated, setTestCommandsUpdated] = useState(false);

  const newTestDocRef = useMemo(() => {
    return test == null
      ? doc(collection(customFirestore, "custom-tests"))
      : null;
  }, []);

  const [provider, setProvider] = useState<TiptapCollabProvider | null>(null);
  const [document, setDocument] = useState<Y.Doc | null>(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    const document = new Y.Doc();
    let organisationId = test?.organisationId ?? buildData.organisationId;
    if (organisationId === "e12973d98ab7ea6399ca9b7692ef6d3af512ac48") {
      organisationId = "b2020aab80a23114934a46d897a4b657d5ae2930";
    }

    const provider = new TiptapCollabProvider({
      name: `${organisationId}/${test?.id ?? newTestDocRef!.id}`,
      appId: "zk5ww0k5",
      token: tipTapJWTData.jwt,
      document,
      onSynced: () => {
        setConnected(true);
      },
      onAuthenticationFailed: (data) => {
        trackSentryException(data);
      },
    });

    setProvider(provider);
    setDocument(document);
    return () => {
      provider.destroy();
    };
  }, []);

  const enrichedPrompts = useMemo(() => {
    const enrichedInteractions = testEditorAssistantState.enrichedInteractions;
    const enrichedFromApiInteractions =
      testEditorAssistantState.enrichedFromApiInteractions;
    const parsedInteractions = Object.keys(enrichedInteractions)
      .sort()
      .reduce<{
        rawInteractions: string[];
        enrichedInteractions: any[];
      }>(
        (prev, currentInteractionId) => {
          const parsedInteractionId = parseInt(currentInteractionId);

          return {
            rawInteractions: [
              ...(prev.rawInteractions != null ? prev.rawInteractions : []),
              ...enrichedInteractions[parsedInteractionId].map(
                (enrichedInteraction) => enrichedInteraction.rawLog
              ),
            ],
            enrichedInteractions: [
              ...(prev.enrichedInteractions != null
                ? prev.enrichedInteractions
                : []),
              ...enrichedInteractions[parsedInteractionId].map(
                (enrichedInteraction, index) =>
                  enrichedInteraction.enrichedLog !== null &&
                  enrichedInteraction.enrichedLog.length > 0
                    ? enrichedInteraction.enrichedLog
                    : enrichedFromApiInteractions[parsedInteractionId]?.[index]
                        .enrichedLog ?? "Generating..."
              ),
            ],
          };
        },
        { rawInteractions: [], enrichedInteractions: [] }
      );

    return generateEnrichedPrompts(
      parsedInteractions.rawInteractions,
      parsedInteractions.enrichedInteractions
    );
  }, [
    testEditorAssistantState.enrichedInteractions,
    testEditorAssistantState.enrichedFromApiInteractions,
  ]);

  const queryClient = useQueryClient();
  const settingsFormRef = useRef<FormikProps<TestSettings>>(null);
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>(
    test?.settings?.mediaFiles ?? []
  );
  const [isDirty, setIsDirty] = useState(false);

  const {
    startExecution,
    stopExecution,
    resetEditor,
    parsedExecutionLogs,
    executionState,
  } = useExecuteSteps({ companyData, buildData });

  const stopExecutionHandler = useCallback(() => {
    if (stopExecution != null) {
      stopExecution();
      trackAmplitudeEvent({
        eventType: `Run execution manually stopped`,
        eventProperties: {
          testId: test?.id,
        },
        groups: { organisation: companyData.name },
      });
    }
  }, [stopExecution, companyData]);

  const sortedInteractionIds = useMemo(
    () =>
      Object.keys(testEditorAssistantState.promptGenerationScreenshots)
        .sort()
        .reverse(),
    [testEditorAssistantState.promptGenerationScreenshots]
  );

  const promptGenerationRecordedActions: CachedScreenData[] = useMemo(() => {
    const promptGenerationScreenshots =
      testEditorAssistantState.promptGenerationScreenshots;
    const enrichedFromApiInteractions =
      testEditorAssistantState.enrichedFromApiInteractions;

    return sortedInteractionIds
      .map((interactionId) => {
        const parsedInteractionId = parseInt(interactionId);

        return promptGenerationScreenshots[parsedInteractionId]?.map(
          (_, index) => {
            const invertedIndex =
              promptGenerationScreenshots[parsedInteractionId].length -
              1 -
              index;
            const enrichedInteraction =
              enrichedFromApiInteractions[parsedInteractionId]?.[invertedIndex];
            const appetizeCommand = enrichedInteraction?.rawLog
              .replace("tap at", "tabOn:")
              .replace("type", "type:");
            const stepPrompt = enrichedInteraction?.enrichedLog;
            const stepNumber = enrichedPrompts.indexOf(stepPrompt) + 1;

            return {
              screenshotUrl:
                promptGenerationScreenshots[parsedInteractionId]?.[
                  invertedIndex
                ],
              gptCommand: enrichedInteraction?.rawLog,
              appetizeCommand,
              prompt: `${stepNumber}. ${stepPrompt}`,
              stepNumber,
              reasoning: `Reasoning: performed action according to this step description: ${stepPrompt}`,
            };
          }
        );
      })
      .flat()
      .reverse();
  }, [
    testEditorAssistantState.promptGenerationScreenshots,
    testEditorAssistantState.enrichedFromApiInteractions,
  ]);

  // Preflight gate for the play button when in markdown mode. Returns
  // `true` if execution should proceed (either because the doc already
  // has the required headings, or because the user clicked "Just Run" /
  // accepted the optimisation), and `false` if it should abort.
  const runPreflightGate = useCallback(async (): Promise<boolean> => {
    if (!markdownMode) return true;

    // Step 1: LLM validation. Before we offer to optimise the content, run
    // a fast LLM pre-flight that flags typical issues (missing test data,
    // requested platform features we don't support, etc). If the LLM
    // surfaces issues, hand off to a dedicated review screen so the user
    // can fix them in place — we'd rather catch this here than after the
    // optimise+run pipeline has already burned time.
    setValidatingTestContent(true);
    try {
      const initialDoc =
        mainTipTapEditor?.state.doc.toJSON() ??
        testEditorProperties.testCommands?.toJSON?.() ??
        null;
      const currentMarkdown = tiptapJsonToMarkdown(initialDoc);
      if (currentMarkdown.trim().length > 0) {
        const validation = await fetchTestContentValidation({
          organisationId: buildData.organisationId,
          markdown: currentMarkdown,
          platform: sessionConfig.platform,
        });
        if (validation.issues.length > 0) {
          const result = await new Promise<{
            choice: ValidationChoice;
            updatedMarkdown: string;
            markdownChanged: boolean;
          }>((resolve) => {
            setValidationDialog({
              open: true,
              markdown: currentMarkdown,
              issues: validation.issues,
              resolve,
            });
          });
          setValidationDialog({
            open: false,
            markdown: "",
            issues: [],
            resolve: null,
          });
          if (result.choice === "cancel") {
            return false;
          }
          if (result.markdownChanged && mainTipTapEditor != null) {
            mainTipTapEditor.commands.setContent(
              markdownToHtml(result.updatedMarkdown)
            );
            setTestEditorProperties((prev) => ({
              ...prev,
              testCommands: mainTipTapEditor.state.doc,
            }));
            setTestCommandsUpdated(true);
          }
        }
      }
    } catch (e) {
      // Validation must not block runs on backend / network errors. We log
      // it but let the user proceed to the existing preflight.
      trackSentryException(e);
    } finally {
      setValidatingTestContent(false);
    }

    // Step 2: existing goals/steps heading check + optimise prompt.
    const editorDoc =
      mainTipTapEditor?.state.doc.toJSON() ??
      testEditorProperties.testCommands?.toJSON?.() ??
      null;
    if (docHasRequiredMarkdownHeadings(editorDoc)) return true;

    const choice = await new Promise<PreflightChoice>((resolve) => {
      setPreflightDialog({ open: true, optimizing: false, resolve });
    });
    if (choice === "cancel") {
      setPreflightDialog({ open: false, optimizing: false, resolve: null });
      return false;
    }
    if (choice === "just-run") {
      setPreflightDialog({ open: false, optimizing: false, resolve: null });
      return true;
    }

    // Optimize: keep the dialog visible (with spinner) while we call
    // /convert_to_markdown, replace the editor doc with the response,
    // then close the dialog and let the run continue.
    setPreflightDialog((prev) => ({ ...prev, optimizing: true }));
    const currentMarkdown = tiptapJsonToMarkdown(editorDoc);
    try {
      const response = await apiClient.post<{ markdown: string }>(
        "/convert_to_markdown",
        {
          org_id: buildData.organisationId,
          content_type: "text",
          text: currentMarkdown,
        }
      );
      const optimized = response.data?.markdown ?? "";
      if (optimized.length > 0 && mainTipTapEditor != null) {
        mainTipTapEditor.commands.setContent(markdownToHtml(optimized));
        setTestEditorProperties((prev) => ({
          ...prev,
          testCommands: mainTipTapEditor.state.doc,
        }));
        setTestCommandsUpdated(true);
      }
      setPreflightDialog({ open: false, optimizing: false, resolve: null });
      return true;
    } catch (e) {
      trackSentryException(e);
      setPreflightDialog({ open: false, optimizing: false, resolve: null });
      setCustomAlertState({
        open: true,
        type: "error",
        title: "Optimization failed",
        description:
          (e as any)?.response?.data?.detail ??
          (e as Error)?.message ??
          "Could not optimize the test content.",
        maintainDisplayed: false,
      });
      return false;
    }
  }, [
    markdownMode,
    mainTipTapEditor,
    testEditorProperties.testCommands,
    buildData.organisationId,
    sessionConfig.platform,
    setCustomAlertState,
    setTestEditorProperties,
  ]);

  const setMarkdownGlobalTask = useCallback(
    (runs: RunExecutionData[]) => {
      if (!markdownMode) return;
      const markdown = tiptapJsonToMarkdown(
        mainTipTapEditor?.state.doc.toJSON() ??
          testEditorProperties.testCommands?.toJSON?.() ??
          null
      );
      const envVars = companyData.settings.envVars ?? [];

      runs.forEach((run, runIndex) => {
        const runInputValues = getInputValues({
          currentRun: run,
          nextRunsToExecute: runs.slice(runIndex + 1),
          envVars,
        });

        // Each run is driven entirely by its own `globalTestTask`. Dependency
        // runs carry their own authored source on `run.commands` and must use
        // it here - otherwise the dependency box executes the parent's markdown
        // task (running the whole flow and getting saved as the dependency
        // baseline). The parent run has no `commands`, so it falls back to the
        // live editor markdown, which may contain unsaved edits.
        let runMarkdown = markdown;
        if (run.commands != null) {
          let depMarkdown = "";
          try {
            depMarkdown = tiptapJsonToMarkdown(JSON.parse(run.commands));
          } catch {
            depMarkdown = "";
          }
          // Only treat a dependency as markdown-driven when its own source is
          // actually markdown (has an ATX heading). A step-based dependency
          // under a markdown parent keeps running via its `promptSteps`.
          if (!/^#{1,6}\s+\S/m.test(depMarkdown)) return;
          runMarkdown = depMarkdown;
        }

        run.globalTestTask = parseMarkdownTemplate({
          markdown: runMarkdown,
          envVars,
          runInputValues,
          currentTemplateTimestamp: { current: null },
          randomValuesTemplates: { current: {} },
        });
        // Keep the un-rendered template so the baseline-save path can align
        // it against `globalTestTask` and recover dynamic values for cache
        // templatization.
        run.globalTestTaskTemplate = runMarkdown;
      });
    },
    [
      markdownMode,
      mainTipTapEditor,
      testEditorProperties.testCommands,
      companyData.settings.envVars,
    ]
  );

  const executeMutation = useMutation({
    mutationKey: ["executeMutation"],
    mutationFn: async (partialPromptSteps: PromptStep[] | void) => {
      const shouldProceed = await runPreflightGate();
      if (!shouldProceed) return;
      const isPartialExecution = !(partialPromptSteps == null);
      resetEditor();
      setIsExecutingFullTest(!isPartialExecution);
      setActiveTab("logs");
      // Snapshot the pre-run baseline so the editor keeps showing it during
      // and after the run, even though the run itself overwrites cachedSteps
      // in Firestore. Without this, the displayed baseline collapses into the
      // current execution as soon as the test succeeds and they look identical.
      setAuxLocalLastSuccessfulRun(localBaseline);
      successCleanupDoneRef.current = false;
      setTestBuilderCustomAlertState({ open: false });

      const parentBaseline = localBaseline.filter(
        (item) => item.title === "parent"
      );
      const baselineToUse =
        parentBaseline.length > 0 ? parentBaseline : localBaseline;

      const promptStepsToUse =
        partialPromptSteps != null && partialPromptSteps?.length > 0
          ? [...partialPromptSteps, succeedingStep]
          : promptSteps;

      const runs = await getRuns({
        steps: promptStepsToUse,
        companyData,
        sessionConfig,
        isPartialExecution,
        test,
        baseline: baselineToUse,
        buildData,
      });
      setMarkdownGlobalTask(runs);
      console.log("runs", runs);
      startExecution({ runs });
    },
  });

  const executeWithDependenciesMutation = useMutation({
    mutationKey: ["executeWithDependenciesMutation"],
    mutationFn: async (upperPromptStep: PromptStep | void) => {
      const shouldProceed = await runPreflightGate();
      if (!shouldProceed) return [];
      const isPartialExecution = upperPromptStep != null;
      resetEditor();
      // See the matching comment in `executeMutation` — snapshot baseline at
      // run start so the editor keeps showing the pre-run state.
      setAuxLocalLastSuccessfulRun(localBaseline);
      successCleanupDoneRef.current = false;
      setTestBuilderCustomAlertState({ open: false });
      setActiveTab("logs");
      setIsExecutingFullTest(!isPartialExecution);

      const nestedDependencies = await getNestedDependencies({
        cachingEnabled: companyData.settings.cachingEnabled ?? false,
        testId: dependencyFormRef.current!.values.dependencyId!,
        tempDependencies: [],
        platform: sessionConfig.platform,
        device: sessionConfig.device,
        language: sessionConfig.language,
      });

      const parsedNestedDependencies = nestedDependencies.map<RunExecutionData>(
        (dependency, index) =>
          // @ts-expect-error
          ({
            ...dependency,
            order: index + 1,
            locale: sessionConfig.language,
            orientation: sessionConfig.orientation,
            os: sessionConfig.os,
          })
      );

      let filteredPromptSteps = promptSteps;

      if (isPartialExecution) {
        filteredPromptSteps = [];
        for (const promptStep of promptSteps) {
          if (promptStep.id === upperPromptStep.id) {
            break;
          }
          filteredPromptSteps.push(promptStep);
        }
        filteredPromptSteps.push(succeedingStep);
      }

      const runs = await getRuns({
        parsedNestedDependencies,
        steps: filteredPromptSteps,
        companyData,
        sessionConfig,
        isPartialExecution,
        test,
        baseline: localBaseline,
        buildData,
      });

      setMarkdownGlobalTask(runs);
      console.log("runs", runs);
      startExecution({ runs });

      return parsedNestedDependencies;
    },
    onError: (error) => {
      console.error(error);
    },
  });

  // Derive the nested dependency baseline chain (dep2 -> dep1 -> ...) from
  // `dependenciesForTest.data`, which is already loaded at editor open. We
  // follow each dependency's first-listed dependency and recurse, mirroring
  // the runtime logic in `getNestedDependencies`. This lets us render the
  // dependency baselines in the execution log immediately, instead of only
  // after the dependency runs populate `test.localDependenciesCacheSteps`.
  const dependenciesBaselineFromAtom: CachedScreenData[] = useMemo(() => {
    if (test == null) return [];
    if (selectedDependencyId == null) return [];
    const allTests = dependenciesForTest?.data;
    if (allTests == null || allTests.length === 0) return [];

    const platform = sessionConfig.platform;
    const device = sessionConfig.device;

    const result: CachedScreenData[] = [];
    const visited = new Set<string>();

    const collect = (testId: string) => {
      if (visited.has(testId)) return;
      visited.add(testId);

      const depTest = allTests.find((t) => t.id === testId);
      if (depTest == null) return;

      // Recurse into the deepest dependency first so the resulting list is
      // ordered deepest-first (dep2, dep1, ...) - the same order the runtime
      // execution path produces.
      const childDepId = depTest.dependencies?.[0]?.testId;
      if (childDepId != null) {
        collect(childDepId);
      }

      const cachedSteps =
        depTest.cachedSteps?.[platform]?.[device] ?? [];
      for (const step of cachedSteps) {
        result.push({ ...step, title: depTest.title });
      }
    };

    collect(selectedDependencyId);
    return result;
  }, [
    test,
    selectedDependencyId,
    dependenciesForTest?.data,
    sessionConfig.platform,
    sessionConfig.device,
  ]);

  const localBaseline: CachedScreenData[] = useMemo(() => {
    // Merge two sources of dependency baselines per-dep (keyed by title), so
    // every dep in the chain stays visible during a run:
    // - `localDependenciesCacheSteps` - appended to as each dep finishes in
    //   this session; latest authoritative baseline for those deps.
    // - `dependenciesBaselineFromAtom` - deep-first chain derived from the
    //   org's tests fetched at editor open; covers deps that haven't run yet.
    // A naive "use one or the other" flips to local-only as soon as the
    // first (deepest) dep finishes, hiding still-pending deps until they
    // also complete.
    const local = test?.localDependenciesCacheSteps ?? [];
    const localByTitle = new Map<string, CachedScreenData[]>();
    const localTitleOrder: string[] = [];
    for (const item of local) {
      if (!localByTitle.has(item.title)) {
        localByTitle.set(item.title, []);
        localTitleOrder.push(item.title);
      }
      localByTitle.get(item.title)!.push(item);
    }

    const dependenciesCacheScreenData: CachedScreenData[] = [];
    const insertedTitles = new Set<string>();
    for (const atomItem of dependenciesBaselineFromAtom) {
      const title = atomItem.title;
      if (localByTitle.has(title)) {
        if (!insertedTitles.has(title)) {
          dependenciesCacheScreenData.push(...localByTitle.get(title)!);
          insertedTitles.add(title);
        }
      } else {
        dependenciesCacheScreenData.push(atomItem);
      }
    }
    for (const title of localTitleOrder) {
      if (!insertedTitles.has(title)) {
        dependenciesCacheScreenData.push(...localByTitle.get(title)!);
        insertedTitles.add(title);
      }
    }

    if (
      test != null &&
      test.cachedSteps != null &&
      test.cachedSteps?.[sessionConfig.platform]?.[sessionConfig.device]
        ?.length > 0
    ) {
      console.log(
        `>> Using deviceSpecificCachedSteps (${sessionConfig.platform} -> ${sessionConfig.device}) as local baseline`
      );

      return [
        ...dependenciesCacheScreenData,
        ...test.cachedSteps?.[sessionConfig.platform]?.[
          sessionConfig.device
        ].map((cachedScreen) => ({
          ...cachedScreen,
          title: "parent",
        })),
      ];
    }

    if (localLastSuccessfulRun.length > 0) {
      console.log(">> Using localLastSuccessfulRun as local baseline");
      return localLastSuccessfulRun;
    }

    if (promptGenerationRecordedActions.length > 0) {
      console.log(">> Using promptGenerationRecordedActions as local baseline");
      return promptGenerationRecordedActions;
    }

    // Even if the parent test has no baseline yet, surface any dependency
    // baselines we can derive so the user can see them grouped in the log.
    if (dependenciesCacheScreenData.length > 0) {
      console.log(
        ">> Using dependency-only baselines (parent has no baseline yet)"
      );
      return dependenciesCacheScreenData;
    }

    console.log(">> Local baseline changed to []");
    return [];
  }, [
    test?.localDependenciesCacheSteps,
    promptGenerationRecordedActions,
    test?.cachedSteps,
    localLastSuccessfulRun,
    dependenciesBaselineFromAtom,
    sessionConfig.platform,
    sessionConfig.device,
  ]);

  useEffect(() => {
    if (
      isExecutingFullTest &&
      executionState.result === "Success" &&
      !successCleanupDoneRef.current
    ) {
      successCleanupDoneRef.current = true;

      if (test?.id != null && test?.localDependenciesCacheSteps != null) {
        queryClient.setQueryData(["getTestForTestEditor", test.id], (prev) => ({
          ...prev,
          localDependenciesCacheSteps: null,
        }));
      }

      executeWithDependenciesMutation.reset();

      if (parsedExecutionLogs.length > 0) {
        setLocalLastSuccessfulRun(parsedExecutionLogs);
      }
    }
  }, [executionState.result]);

  const navigate = useNavigate();

  // Navigate from the markdown editor to the standard editor for the same
  // test (or to the build's "new test" entry when there is no test yet).
  const switchToStandardEditor = useCallback(() => {
    if (test?.id != null) {
      navigate(`/gpt-driver/build/${buildId}/test/${test.id}`);
    } else if (buildId != null) {
      navigate(`/gpt-driver/build/${buildId}`);
    }
  }, [navigate, buildId, test?.id]);

  const navigateTo = useCallback(
    (path: string) => {
      navigate(path);
    },
    [navigate]
  );

  useEffect(() => {
    if (isEditing && inputRef.current != null) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleUpdateTitleOnKeyBlur = (
    e: React.FocusEvent<HTMLInputElement>
  ) => {
    setIsEditing(false);
    setTestEditorProperties((prev) => ({
      ...prev,
      recordingTitle: e.target.value,
    }));
  };

  const handleUpdateTitleOnKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Enter") {
      e.preventDefault(); // Prevent the default behavior of submitting the form
      setIsEditing(false);
      setTestEditorProperties((prev) => ({
        ...prev,
        recordingTitle: (e.target as HTMLInputElement).value,
      }));
    }
  };

  const [shouldForceRedirect, setShouldForceRedirect] = useState(false);

  const redirectBlocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !testEditorNotDirty &&
      !shouldForceRedirect &&
      currentLocation.pathname !== nextLocation.pathname
  );

  const saveTestMutation = useMutation({
    mutationKey: ["saveTestMutation"],
    mutationFn: async () => {
      // =================================================================
      // SUBPROCESS 1: UPLOAD ENRICHED LOGS AND SCREENSHOTS
      // This function handles uploading interaction logs and screenshots
      // to Firebase Storage, and manages cleanup of old screenshots
      // =================================================================
      const uploadEnrichedLogs = async (
        testId: string,
        hadEnrichedInteractionsLogsPath: boolean
      ) => {
        // Extract enriched interaction data from the test editor state
        const promptGenerationScreenshots =
          testEditorAssistantState.promptGenerationScreenshots;
        const enrichedFromApiInteractions =
          testEditorAssistantState.enrichedFromApiInteractions;
        const enrichedInteractions =
          testEditorAssistantState.enrichedInteractions;

        let newEnrichedInteractions = {};

        // Create reference to the enriched interaction logs storage location
        const enrichedInteractionLogsRef = ref(
          customStorage,
          `enrichedInteractionLogs/${testId}/logs.json`
        );

        // =================================================================
        // SUBPROCESS 1A: IDENTIFY OLD SCREENSHOTS FOR CLEANUP
        // Build array of old screenshot references that need to be deleted
        // =================================================================
        let oldScreenshotsRefs: string[] = [];
        if (test?.promptGenerationScreenshots != null) {
          // Compare existing screenshots with new ones to identify orphaned files
          oldScreenshotsRefs = Object.keys(
            test.promptGenerationScreenshots
          ).reduce<string[]>((prev, current) => {
            const newScreenshots =
              promptGenerationScreenshots[parseInt(current)];
            const oldScreenshots =
              test.promptGenerationScreenshots![parseInt(current)];

            return [
              ...prev,
              // If no new screenshots for this index, mark old ones for deletion
              ...(newScreenshots == null
                ? oldScreenshots.map(
                    (screenshotUrl) =>
                      ref(customStorage, screenshotUrl).fullPath
                  )
                : []),
            ];
          }, []);
        }

        // Get reference to the test document in Firestore
        const docRef = doc(customFirestore, "custom-tests", testId);

        // =================================================================
        // SUBPROCESS 1B: PROCESS AND UPLOAD NEW SCREENSHOTS
        // Upload screenshots to storage and create enriched interaction logs
        // =================================================================
        if (enrichedPrompts.length > 0) {
          // Process each set of screenshots and create enriched interaction data
          newEnrichedInteractions = await Object.keys(
            promptGenerationScreenshots
          ).reduce<
            Promise<
              Record<number, Array<{ rawLog: string; enrichedLog: string }>>
            >
          >(async (prev, current) => {
            const currentIndex = parseInt(current);

            // Upload all screenshots for this interaction index
            const screenshotUrls = await Promise.all(
              promptGenerationScreenshots[currentIndex].map(
                async (screenshot, index) => {
                  // Create unique storage path for each screenshot
                  const screenshotRef = ref(
                    customStorage,
                    `enrichedInteractionLogs/${testId}/screenshot-${currentIndex}-${
                      index + 1
                    }.png`
                  );
                  // Convert base64 screenshot to blob and upload
                  const screenshotBlob = await b64toBlob(screenshot);
                  await uploadBytes(screenshotRef, screenshotBlob);
                  return await getDownloadURL(ref(screenshotRef));
                }
              )
            );

            // Combine previous results with current interaction data
            const awaitedPrev = await prev;
            return {
              ...awaitedPrev,
              [currentIndex]: promptGenerationScreenshots[currentIndex].map(
                (_, index) => ({
                  // Merge enriched interaction data with screenshot URLs
                  ...(enrichedFromApiInteractions[currentIndex]?.[index] ??
                    enrichedInteractions[currentIndex][index]),
                  screenshotUrl: screenshotUrls[index],
                })
              ),
            };
          }, Promise.resolve({}));

          // Upload the complete enriched interactions data as JSON
          await uploadString(
            enrichedInteractionLogsRef,
            JSON.stringify(newEnrichedInteractions)
          );

          // Update test document with reference to the uploaded logs
          await updateDoc(docRef, {
            enrichedInteractionsLogsPath: enrichedInteractionLogsRef.fullPath,
          });
        }
        // =================================================================
        // SUBPROCESS 1C: CLEANUP WHEN NO ENRICHED PROMPTS
        // Remove existing enriched logs if no new prompts are present
        // =================================================================
        else if (hadEnrichedInteractionsLogsPath) {
          try {
            // Delete existing enriched interaction logs
            await deleteObject(
              ref(customStorage, enrichedInteractionLogsRef.fullPath)
            );
            // Remove the reference from the test document
            await updateDoc(docRef, {
              enrichedInteractionsLogsPath: deleteField(),
            });
          } catch (e) {
            trackSentryException(e);
            throw e;
          }
        }

        // =================================================================
        // SUBPROCESS 1D: CLEANUP OLD SCREENSHOTS
        // Delete orphaned screenshot files from storage
        // =================================================================
        if (oldScreenshotsRefs.length > 0) {
          await Promise.all(
            oldScreenshotsRefs.map(async (oldScreenshotRef) => {
              await deleteObject(ref(customStorage, oldScreenshotRef));
            })
          );
        }
      };

      try {
        // =================================================================
        // SUBPROCESS 2: FORM VALIDATION
        // Validate settings form before proceeding with save operation
        // =================================================================
        if (Object.keys(settingsFormRef.current?.errors ?? {}).length > 0) {
          settingsFormRef.current?.submitForm();
          setCustomAlertState({
            open: true,
            type: "error",
            title: "Please fix the settings errors before saving the test",
          });
          return;
        }

        // =================================================================
        // SUBPROCESS 3: VERSION CONTROL
        // Create a version snapshot of the current state
        // =================================================================
        provider?.createVersion(user.email);

        // Extract test settings from the form, with defaults
        const testSettings = {
          ...(settingsFormRef.current?.values ?? {
            testInputs: [],
            launchParams: [],
            preRequests: [],
            teardownRequests: [],
          }),
          mediaFiles,
        };

        let newTestId: string;

        // =================================================================
        // SUBPROCESS 4A: CREATE NEW TEST
        // Handle creation of a brand new test document
        // =================================================================
        if (test == null) {
          // Create new test document with all required fields
          await setDoc(newTestDocRef!, {
            title: testEditorProperties.recordingTitle,
            commands: JSON.stringify(testEditorProperties.testCommands),
            userId: user.uid,
            organisationId: buildData.organisationId,
            updatedBy: currentActor(),
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
            tag: "",
            type: "gpt",
            uploadId: buildId,
            settings: testSettings,
            dependencyInputValues: {
              // Include dependency input values if a dependency is selected
              ...(selectedDependencyId != null && {
                [selectedDependencyId]:
                  dependencyFormRef.current?.values?.dependencyInputValues ??
                  [],
              }),
            },
          });

          newTestId = newTestDocRef!.id;

          // =================================================================
          // SUBPROCESS 4A1: CACHE LAST SUCCESSFUL RUN
          // Store screenshots and data from the most recent successful test run
          // =================================================================

          console.log("localLastSuccessfulRun", localLastSuccessfulRun);
          if (localLastSuccessfulRun.length > 0) {
            const localLastSuccessfulRunToBeCached: CachedScreenData[] =
              await Promise.all(
                localLastSuccessfulRun.map(async (value, index) => {
                  // Upload screenshot to dedicated cache storage location
                  const screenshotLink = await uploadScreenshotToStorage(
                    value.screenshotUrl,
                    `cachedScreenshots/${newTestId}/${sessionConfig.platform}-${
                      sessionConfig.device
                    }-cachedScreenshot-${index + 1}.jpg`
                  );

                  let voiceCommandFirebaseStorageUrl: string | undefined;

                  console.log("uploading value", value);

                  // Handle voice command uploads if present
                  if (value.voiceCommandUrl != null) {
                    const data = await downloadAndUploadToStorage({
                      url: value.voiceCommandUrl,
                      storagePath: `voiceCommands/${newTestId}/${
                        sessionConfig.platform
                      }-${sessionConfig.device}-cachedScreenshot-${
                        index + 1
                      }.jpg`,
                    });

                    console.log("uploaded voice command", data);

                    voiceCommandFirebaseStorageUrl = data.downloadURL;
                  }

                  // Filter out properties not needed in cached data
                  const {
                    annotatedScreenshotUrl,
                    serverExecutionParams,
                    ...filteredCacheStep
                  } = value;

                  console.log("filteredCacheStep step to be saved", {
                    ...filteredCacheStep,
                    screenshotUrl: screenshotLink,
                  });

                  return {
                    ...filteredCacheStep,
                    screenshotUrl: screenshotLink,
                    // Include voice command URL if it was uploaded
                    ...(voiceCommandFirebaseStorageUrl && {
                      voiceCommandUrl: voiceCommandFirebaseStorageUrl,
                    }),
                  };
                })
              );

            // Update test document with cached steps, organized by platform and device
            await updateDoc(newTestDocRef!, {
              cachedSteps: {
                [sessionConfig.platform]: {
                  [sessionConfig.device]:
                    localLastSuccessfulRunToBeCached ?? [],
                },
              },
            });
          }

          // Upload enriched logs for the new test
          await uploadEnrichedLogs(newTestId, false);

          // =================================================================
          // SUBPROCESS 4A2: HANDLE TEST DEPENDENCIES
          // Create dependency relationship if a dependency is selected
          // =================================================================
          if (selectedDependencyId != null) {
            await addDoc(
              collection(
                customFirestore,
                `custom-tests/${newTestDocRef!.id}/dependencies`
              ),
              {
                order: 1,
                testId: selectedDependencyId,
              }
            );
          }

          // Track test creation event for analytics
          trackAmplitudeEvent({
            eventType: "Save Test",
            eventProperties: { testId: newTestId, type: "create" },
            groups: { organisation: companyData.name },
          });
        }
        // =================================================================
        // SUBPROCESS 4B: UPDATE EXISTING TEST
        // Handle updates to an existing test document
        // =================================================================
        else {
          const docRef = doc(customFirestore, "custom-tests", test.id);

          // Create field path for dependency input values update
          const dependencyInputValuesToUpdate = `dependencyInputValues.${selectedDependencyId}`;

          // Prepare update data
          const updatedTest = {
            title: testEditorProperties.recordingTitle,
            commands: JSON.stringify(testEditorProperties.testCommands),
            settings: testSettings,
            // Include dependency input values if present
            ...(selectedDependencyId != null &&
              dependencyFormRef.current?.values?.dependencyInputValues !=
                null && {
                [dependencyInputValuesToUpdate]:
                  dependencyFormRef.current?.values?.dependencyInputValues,
              }),
            updatedBy: currentActor(),
            updatedAt: Timestamp.now(),
          };

          // =================================================================
          // SUBPROCESS 4B1: HANDLE FOLDER-BASED TESTS
          // Special transaction logic for tests that belong to folders
          // =================================================================
          if (test.folderId) {
            const shouldReloadTestList = await runTransaction(
              customFirestore,
              async (transaction) => {
                let folderActuallyExists = false;

                // Get the current test data
                const lastTestValue = (
                  await transaction.get(docRef)
                ).data() as Test;

                // If test belongs to a folder, update the folder's test preview
                if (lastTestValue.folderId != null) {
                  folderActuallyExists = true;

                  const folderDocRef = doc(
                    customFirestore,
                    "folders",
                    lastTestValue.folderId
                  );
                  const folderSnapshot = await transaction.get(folderDocRef);
                  const folderData = folderSnapshot.data() as TestFolder;

                  // Find and update the test preview in the folder
                  const testPreviewIndex =
                    folderData.tests?.findIndex(
                      (testPreview) => testPreview.id === test.id
                    ) ?? -1;

                  if (testPreviewIndex != -1) {
                    const newTests = replaceItemFomArray(
                      folderData.tests!,
                      testPreviewIndex,
                      {
                        id: test.id,
                        title: updatedTest.title,
                        updatedAt: updatedTest.updatedAt,
                      }
                    );

                    transaction.update(folderDocRef, { tests: newTests });
                  }
                }

                // Update the main test document
                transaction.update(docRef, updatedTest);
                return folderActuallyExists;
              }
            );

            // Invalidate caches if folder was updated
            if (shouldReloadTestList) {
              await queryClient.invalidateQueries({
                queryKey: ["getFolders"],
              });
              getTestsAtom.remove({
                organizationId: buildData.organisationId,
                folderId: test.folderId,
              });
            }
          } else {
            // Simple update for tests not in folders
            await updateDoc(docRef, updatedTest);
          }

          // Upload enriched logs for the updated test
          await uploadEnrichedLogs(
            test.id,
            test.enrichedInteractionsLogsPath != null
          );

          // =================================================================
          // SUBPROCESS 4B2: MANAGE DEPENDENCY CHANGES
          // Handle adding, updating, or removing test dependencies
          // =================================================================
          if (selectedDependencyId != test?.dependencies?.[0]?.testId) {
            if (test?.dependencies?.[0]?.testId != null) {
              // Existing dependency found
              const dependencyRef = doc(
                customFirestore,
                "custom-tests",
                test.id,
                "dependencies",
                test.dependencies[0].id
              );

              if (selectedDependencyId == null) {
                // Remove dependency if none selected
                await deleteDoc(dependencyRef);
              } else {
                // Update existing dependency
                await updateDoc(dependencyRef, {
                  order: 1,
                  testId: selectedDependencyId,
                });
              }
            } else {
              // No existing dependency, add new one if selected
              if (selectedDependencyId != null) {
                await addDoc(
                  collection(
                    customFirestore,
                    `custom-tests/${test.id}/dependencies`
                  ),
                  {
                    order: 1,
                    testId: selectedDependencyId,
                  }
                );
              }
            }
          }

          // Track test update event for analytics
          trackAmplitudeEvent({
            eventType: "Save Test",
            eventProperties: { testId: test.id, type: "update" },
            groups: { organisation: companyData.name },
          });
        }

        // =================================================================
        // SUBPROCESS 5: POST-SAVE CLEANUP AND NAVIGATION
        // Reset UI state and handle navigation after successful save
        // =================================================================

        // Reset dirty state flags
        setTestCommandsUpdated(false);
        setIsDirty(false);

        // Clear cached test data to force refresh
        getTestsAtom.remove({
          organizationId: buildData.organisationId,
          folderId: kAllTestsId,
        });
        queryClient.removeQueries({ queryKey: ["getTests"] });
        await queryClient.invalidateQueries({
          queryKey: ["getTestForTestEditor"],
        });

        // =================================================================
        // SUBPROCESS 6: HANDLE NAVIGATION AND REDIRECTS
        // Manage navigation flow based on redirect blocker state
        // =================================================================
        if (redirectBlocker.state == "blocked") {
          // If navigation was blocked, now proceed with it
          redirectBlocker.proceed?.();
        } else if (test == null) {
          // For new tests, navigate to the test editor after a delay
          setShouldForceRedirect(true);
          setTimeout(() => {
            appetizeSessionStore.setState((prev) => ({
              getData: () => null,
              startedAt: null,
            }));
            buildLogsAtomWithId.remove(buildId);
            navigateTo(
              `/gpt-driver/build/${buildId}/test/${newTestId!}${markdownMode ? "/markdown" : ""}`
            );
          }, 500);
        }
      } catch (error) {
        // =================================================================
        // SUBPROCESS 7: ERROR HANDLING
        // Handle and report any errors that occur during the save process
        // =================================================================
        if (redirectBlocker.state == "blocked") {
          redirectBlocker.reset();
        }

        // Report error to monitoring service
        trackSentryException(error);
        console.error("Error saving recording: ", error);

        // Show error message to user
        setCustomAlertState({
          open: true,
          type: "error",
          title: "Something went wrong",
          description: getErrorMessage(error),
        });
      }
    },
  });

  const flattenSteps: ExecutionStepState[] = useMemo(() => {
    return executionState.runs.map((run) => run.executionSteps).flat();
  }, [executionState.runs]);

  const prepareCSVData = useCallback(() => {
    const extractTargetValue = (command: string) => {
      if (command.includes("tabOn")) {
        const coordinates = getDigitsAfterFirstSemicolon(command);
        if (coordinates !== null) {
          let [xPos, yPos] = coordinates;
          [xPos, yPos] = [Number(xPos), Number(yPos)];
          return `.*tabOn:.*(${xPos - 1}|${xPos}|${xPos + 1}).*(${
            yPos - 1
          }|${yPos}|${yPos + 1}).*`;
        }
      } else if (command.includes("task complete")) {
        return ".*task complete:.*";
      }
      return `.*${command}.*`;
    };

    const promptSteps = getPromptStepsFromTipTapNode({
      node: testEditorProperties.testCommands,
    });
    const parsedCommands = getPromptStepsText(promptSteps);

    const testTask = parsedCommands.replace(
      /{{(.*?)}}/g,
      (match, key) => replaceTemplateItem(key.trim()) || ""
    );
    // prev appetizeSteps = [["example1", "example2"], ["example3", "example4"]]

    const targetValuesArray = flattenSteps
      .map((step) => [extractTargetValue(step.appetizeCommands[0])])
      .map((value) => value.join("\n"));
    const screenContentArray = flattenSteps.map((step) =>
      step.screenContent.join("\n")
    );
    const actionHistoryArray = flattenSteps.map((step) =>
      step.actionHistory.join("\n")
    );
    const labels = targetValuesArray.map(
      (_, index) => `${testEditorProperties.recordingTitle} Step ${index + 1}`
    );

    const rows = [
      ["label", "test_task", "action_history", "screen_content", "target"],
    ];
    for (let i = 0; i < targetValuesArray.length; i++) {
      rows.push([
        labels[i],
        testTask,
        actionHistoryArray[i],
        screenContentArray[i],
        targetValuesArray[i],
      ]);
    }

    return rows;
  }, [flattenSteps, testEditorProperties.testCommands]);

  const exportToCSV = (e) => {
    try {
      const csvData = prepareCSVData();
      const filename = `TestCase_${testEditorProperties.recordingTitle.replace(
        " ",
        "_"
      )}.csv`;
      downloadBlob(arrayToCsv(csvData), filename, "text/csv;charset=utf-8;");
    } catch (e) {
      trackSentryException(e);
      console.error("Error occurred while exporting the test steps: ", e);
      alert(`Error occurred while exporting the test steps: ${e}`);
    }
  };
  const removeCachedSteps = async (
    deleteAll: boolean,
    stepNumber: number = 0
  ) => {
    if (
      test != null &&
      test.id &&
      test.cachedSteps != null &&
      test.cachedSteps?.[sessionConfig.platform]?.[sessionConfig.device]
        ?.length > 0
    ) {
      const updatedCachedSteps = await deleteCachedSteps(
        test.id,
        deleteAll,
        sessionConfig.platform,
        sessionConfig.device,
        stepNumber
      );
      console.log("Updated data", updatedCachedSteps);

      const updatedTest = {
        ...test,
        cachedSteps: updatedCachedSteps,
      };

      queryClient.setQueryData(["getTestForTestEditor", test.id], updatedTest);
    } else {
      if (deleteAll) {
        setTestEditorAssistantState((prev) => ({
          ...prev,
          promptGenerationScreenshots: {},
        }));
        setLocalLastSuccessfulRun([]);
        setAuxLocalLastSuccessfulRun(null);
      } else {
        if (localLastSuccessfulRun.length > 0) {
          const updatedLocalLasSuccessfulRun = localLastSuccessfulRun.filter(
            (value, index) => index !== stepNumber
          );
          setLocalLastSuccessfulRun(updatedLocalLasSuccessfulRun);
        } else if (Object.keys(promptGenerationRecordedActions).length > 0) {
          console.log("Removing assitant step", stepNumber);
          const stepToBeDeleted = promptGenerationRecordedActions[stepNumber];
          const updatedGenerationScreenshots: typeof testEditorAssistantState.promptGenerationScreenshots =
            {};
          console.log({
            promptGenerationScreenshots:
              testEditorAssistantState.promptGenerationScreenshots,
          });
          console.log({ updatedGenerationScreenshots });
          for (const [index, screenshotsList] of Object.entries(
            testEditorAssistantState.promptGenerationScreenshots
          )) {
            updatedGenerationScreenshots[parseInt(index)] =
              screenshotsList.filter(
                (value) => value !== stepToBeDeleted.screenshotUrl
              );
          }
          setTestEditorAssistantState((prev) => ({
            ...prev,
            promptGenerationScreenshots: updatedGenerationScreenshots,
          }));
        }
      }
    }
  };

  const resetPromptGenerator = () => {
    setTestEditorAssistantState({
      enrichedInteractions: {},
      promptGenerationScreenshots: {},
      enrichedFromApiInteractions: {},
    });

    firstPressedKeyTimestamp.current = 0;
  };

  const parsedEnvVars = useMemo(
    () =>
      companyData.settings.envVars?.map((envVar) => `env.${envVar.key}`) ?? [],
    [companyData.settings]
  );

  const annotatedScreenshotsQuery = useQuery({
    queryKey: [
      "annotateAssistantScreenshots",
      testEditorAssistantState.promptGenerationScreenshots,
    ],
    queryFn: async () => {
      const annotatedScreenshots: Record<number, string[]> = {};

      try {
        for (const [sortedInteractionId, screenshotsList] of Object.entries(
          testEditorAssistantState.promptGenerationScreenshots
        )) {
          const parsedInteractionId = parseInt(sortedInteractionId);
          annotatedScreenshots[parsedInteractionId] = [];
          for (const screenshotUrl of screenshotsList) {
            const index = screenshotsList.indexOf(screenshotUrl);
            const appetizeCommand =
              testEditorAssistantState.enrichedInteractions[
                parsedInteractionId
              ][index].rawLog;
            const annotatedScreen = await annotateScreenshot(screenshotUrl, [
              appetizeCommand,
            ]);
            annotatedScreenshots[parsedInteractionId].push(annotatedScreen);
          }
        }

        return annotatedScreenshots;
      } catch (e) {
        console.error(e);
        trackSentryException(e);
        return testEditorAssistantState.promptGenerationScreenshots; // not annotated screenshots
      }
    },
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  const testEditorNotDirty = useMemo(
    () =>
      testEditorProperties.originalRecordingTitle ===
        testEditorProperties.recordingTitle &&
      !testCommandsUpdated &&
      test?.dependencies?.[0]?.testId == selectedDependencyId &&
      !testEditorProperties.hasGeneratedEnrichedLogs &&
      !isDirty,
    [
      testEditorProperties.originalRecordingTitle,
      testEditorProperties.recordingTitle,
      testCommandsUpdated,
      testEditorProperties.hasGeneratedEnrichedLogs,
      isDirty,
      test?.dependencies,
      selectedDependencyId,
    ]
  );

  const isSaveButtonDisabled = useMemo(
    () =>
      saveTestMutation.isPending ||
      testEditorNotDirty ||
      dependenciesForTest.isError,
    [
      saveTestMutation.isPending,
      dependenciesForTest.isError,
      testEditorNotDirty,
    ]
  );

  useEffect(() => {
    setForceUpdateManualConditionsState({
      unsavedTest: saveTestMutation.isPending || !testEditorNotDirty,
    });

    return () => {
      setForceUpdateManualConditionsState({
        unsavedTest: false,
      });
    };
  }, [saveTestMutation.isPending, testEditorNotDirty]);

  const acceptedDynamicValues = useMemo(
    () => [
      "timestamp",
      "currentDate",
      "currentDateFormatted",
      "env.appBundle",
      "env.appVersion",
      ...parsedEnvVars,
      ...(settingsFormRef.current?.values?.inputKeys?.map(
        (input) => input.key
      ) ?? []),
    ],
    [parsedEnvVars, settingsFormRef.current]
  );

  return (
    <ThemeProvider theme={darkTheme}>
      <ThemeProvider theme={lightTheme}>
        <AsyncConfirmationModal
          open={redirectBlocker.state == "blocked"}
          secondaryActionHandler={redirectBlocker.proceed}
          closeHandler={redirectBlocker.reset}
          actionHandler={async () => {
            await saveTestMutation.mutateAsync();
          }}
          submitting={saveTestMutation.isPending}
          title={"Hold on, you have unsaved changes!"}
          description={
            "If you leave now, you'll lose all unsaved changes.\n" +
            "Would you like to save before exiting?"
          }
          actionTitle={"Save & Exit"}
          actionIcon={<Save />}
          backTitle={"Exit Without Saving"}
        />
      </ThemeProvider>
      <HeaderWrapper>
        <div className={"flex gap-2 items-center"}>
          {isEditing ? (
            <input
              ref={inputRef}
              className="border-b-2 border-white text-white bg-transparent focus:outline-none w-full"
              defaultValue={testEditorProperties.recordingTitle}
              onBlur={handleUpdateTitleOnKeyBlur}
              onKeyDown={handleUpdateTitleOnKeyDown}
            />
          ) : (
            <span className="text-white text-lg overflow-hidden overflow-ellipsis">
              {testEditorProperties.recordingTitle}
            </span>
          )}
          <IconButton
            aria-label="edit test name"
            size="medium"
            disabled={isEditing}
            onClick={handleEditClick}
          >
            <BorderColorIcon fontSize={"small"} className={"text-[#7C7C7C]"} />
          </IconButton>
        </div>
        <div className={"flex items-start gap-2 justify-end"}>
          {saveTestMutation.isPending && <RenderSpinner />}
          {(user.companyName === "kitchenful" || user.role === "superUser") && (
            <Tooltip title={"Export execution log as CSV file"} placement="top">
              <span>
                <Button
                  onClick={exportToCSV}
                  variant={"outlined"}
                  size={"large"}
                  disabled={
                    session.getData?.() === null ||
                    saveTestMutation.isPending ||
                    flattenSteps.length === 0
                  }
                >
                  CSV
                </Button>
              </span>
            </Tooltip>
          )}
          <Button
            onClick={() => {
              saveTestMutation.mutate();
            }}
            variant={"outlined"}
            size={"large"}
            disabled={isSaveButtonDisabled}
          >
            {saveTestMutation.isPending ? "Saving..." : "Save"}
          </Button>
        </div>
      </HeaderWrapper>
      <Box sx={{ width: "100%", overflow: "auto" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider", marginTop: "8px" }}>
          <Tabs
            value={activeTab}
            onChange={handleChangeTab}
            aria-label="basic tabs example"
            variant="scrollable"
            scrollButtons={"auto"}
            allowScrollButtonsMobile
            sx={{
              "& .MuiButtonBase-root": {
                minHeight: "0px",
              },
              "& .MuiTabs-scrollButtons": {
                color: "rgba(255,255,255,0.7)",
              },
              ".MuiTabs-scrollButtons.Mui-disabled": {
                opacity: 0.3,
              },
            }}
          >
            <Tab
              label="Prompt"
              icon={<Notes />}
              iconPosition={"start"}
              {...a11yProps("prompt")}
            />
            {
              <Tab
                label="Execution log"
                icon={<Compare />}
                iconPosition={"start"}
                {...a11yProps("logs")}
              />
            }
            {companyData.settings.testSettingsEnabled && (
              <Tab
                label="Settings"
                icon={<Settings />}
                iconPosition={"start"}
                {...a11yProps("settings")}
              />
            )}
            <Tab
              label="Device logs"
              icon={<Terminal />}
              iconPosition={"start"}
              {...a11yProps("device-logs")}
            />
            <Tab
              label="Network logs"
              icon={<Wifi />}
              iconPosition={"start"}
              {...a11yProps("network-logs")}
            />
          </Tabs>
        </Box>
        <CustomTabPanel value={"prompt"} hidden={activeTab !== "prompt"}>
          <Paper sx={{ paddingTop: "8px" }}>
            {!(provider == null) && !(document == null) && connected ? (
              <TipTapEditor
                buildData={buildData}
                testEditorProperties={testEditorProperties}
                setTestCommandsUpdated={setTestCommandsUpdated}
                setTestEditorProperties={setTestEditorProperties}
                running={executionState.isRunning}
                stopExecution={stopExecutionHandler}
                isNewTest={test == null}
                testCommandsUpdated={testCommandsUpdated}
                provider={provider}
                document={document}
                selectedTestDependencyId={selectedDependencyId ?? undefined}
                auxDisplayPasteStep={auxDisplayPasteStep}
                setAuxDisplayPasteStep={setAuxDisplayPasteStep}
                executeMutation={executeMutation}
                executeWithDependenciesMutation={
                  executeWithDependenciesMutation
                }
                userEmail={user.email}
                templates={companyData.templates}
                audios={companyData.audios}
                acceptedDynamicValues={acceptedDynamicValues}
                dependencies={dependenciesForTest?.data}
                settingsData={companyData.settings}
                markdownMode={markdownMode}
                onImportClick={
                  markdownMode ? () => setImportDialogOpen(true) : undefined
                }
                onSwitchToStandardEditor={
                  markdownMode ? switchToStandardEditor : undefined
                }
              />
            ) : (
              <div className={"flex justify-center pb-3.5"}>
                <CircularProgress />
              </div>
            )}
          </Paper>

          <div className={"my-4"}>
            {/* TODO make the Formik handle saving and sending values to firestore and displaying it from test document if the testInputsValues already exist */}
            {dependenciesForTest.isSuccess && (
              <Formik<DependencyFormState>
                innerRef={dependencyFormRef}
                initialValues={{
                  dependencyId: dependencyFormInitialState.selectedDependencyId,
                  dependencyInputValues:
                    dependencyFormInitialState.dependencyInputs,
                }}
                validationSchema={object({
                  dependencyId: string().nullable(),
                  dependencyInputValues: array(
                    object({
                      key: string().required("Required field"),
                      value: string().required("Required field"),
                    })
                  ).default([]),
                }).test(
                  "pre-request-templates-validator",
                  "check if templates values used in curl commands are previously stored",
                  ({ dependencyInputValues }, ctx) => {
                    // New validation rule: Validate environment variables in inputKeys.defaultValue
                    const valueErrors: ValidationError[] =
                      dependencyInputValues.reduce<ValidationError[]>(
                        (prev, current, currentIndex) => {
                          if (!current.value) return prev;

                          const templates = [
                            ...(current.value.match(/{{(.*?)}}/g) ?? []),
                          ].map((match) => match.replace(/{{|}}/g, ""));
                          console.log({ templates });

                          if (
                            templates.length > 0 &&
                            !templates.every((template) =>
                              companyData.settings.envVars
                                .map((envVar) => `env.${envVar.key}`)
                                .includes(template)
                            )
                          ) {
                            return [
                              ...prev,
                              ctx.createError({
                                path: `dependencyInputValues.${currentIndex}.value`,
                                message: `Value contains environment variable that is not defined`,
                              }),
                            ];
                          }
                          return prev;
                        },
                        []
                      );

                    if (valueErrors.length > 0) {
                      return new ValidationError(valueErrors);
                    }
                    return true;
                  }
                )}
                onSubmit={async (values, { setSubmitting, resetForm }) => {
                  resetForm();
                  setSubmitting(false);
                }}
                validateOnBlur={true}
                enableReinitialize={true}
              >
                {({ values, setValues }) => (
                  <Form>
                    <div className={"pl-4  flex flex-col gap-4"}>
                      <div className={"flex flex-col gap-2"}>
                        <h3 className="text-[#7C7C7C] text-[16px] mb-2">
                          Test Dependency
                        </h3>
                        <FormikSelect
                          sx={{ maxWidth: "384px" }}
                          name={"dependencyId"}
                          key={"dependencyId"}
                          label={"Dependency"}
                          optionTitleKey={"title"}
                          valueKey={"id"}
                          options={dependenciesForTest.data}
                          onChange={async (value) => {
                            const newValue = value === "0" ? null : value;
                            await setValues((prev) => ({
                              ...prev,
                              dependencyId: newValue,
                            }));
                            setSelectedDependencyId(newValue);
                          }}
                        />
                      </div>
                      {values.dependencyInputValues.length > 0 && (
                        <div className={"flex flex-col gap-2"}>
                          <h3 className="text-[#7C7C7C] text-[16px] mb-2 ">
                            Required test inputs for this dependency
                          </h3>
                          <div className={"flex flex-col gap-4 items-start"}>
                            <div className={"pt-4 flex flex-col gap-4"}>
                              {values.dependencyInputValues.map(
                                (item, index) => (
                                  <div
                                    key={index}
                                    className={"flex gap-3 items-start"}
                                  >
                                    <FormikTextInput
                                      name={`dependencyInputValues.${index}.key`}
                                      label={"Key"}
                                      placeholder={item.key}
                                      type={"text"}
                                      value={item.key}
                                      disabled
                                    />
                                    <FormikTextInput
                                      name={`dependencyInputValues.${index}.value`}
                                      label={"Value"}
                                      placeholder={"Value"}
                                      type={"text"}
                                      required
                                      value={item.value}
                                      onChange={() => {
                                        setIsDirty(true);
                                      }}
                                    />
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </Form>
                )}
              </Formik>
            )}
          </div>
        </CustomTabPanel>
        <CustomTabPanel value={"logs"} hidden={activeTab !== "logs"}>
          <TestEditorExecutionLogs
            setActiveTab={setActiveTab}
            setAuxDisplayPasteStep={setAuxDisplayPasteStep}
            baseline={auxLocalLastSuccessfulRun ?? localBaseline}
            currentLogs={parsedExecutionLogs ?? []}
            removeCachedSteps={removeCachedSteps}
            running={executionState.isRunning}
            stopExecution={stopExecutionHandler}
            acceptedDynamicValues={acceptedDynamicValues}
            isPreparingData={
              executeMutation.isPending ||
              executeWithDependenciesMutation.isPending
            }
            companyData={companyData}
            sessionConfig={sessionConfig}
            test={test}
            buildId={buildId!}
          />
        </CustomTabPanel>
        {companyData.settings.testSettingsEnabled && (
          <CustomTabPanel value={"settings"} hidden={activeTab !== "settings"}>
            <TestEditorSettings
              formRef={settingsFormRef}
              test={test}
              setIsDirty={setIsDirty}
              envVars={companyData.settings.envVars}
              organisationId={buildData.organisationId}
              mediaFiles={mediaFiles}
              onMediaFilesChange={setMediaFiles}
              newTestId={newTestDocRef?.id}
            />
          </CustomTabPanel>
        )}
        <CustomTabPanel
          value={"device-logs"}
          hidden={activeTab !== "device-logs"}
        >
          <DeviceLogs />
        </CustomTabPanel>
        <CustomTabPanel
          value={"network-logs"}
          hidden={activeTab !== "network-logs"}
        >
          <NetworkLogs />
        </CustomTabPanel>
      </Box>
      {markdownMode && (
        <MarkdownImportDialog
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          onImported={(importedMarkdown) => {
            const editor = mainTipTapEditor;
            if (editor != null) {
              editor.commands.setContent(markdownToHtml(importedMarkdown));
              setTestEditorProperties((prev) => ({
                ...prev,
                testCommands: editor.state.doc,
              }));
              setTestCommandsUpdated(true);
            }
          }}
          organisationId={buildData.organisationId}
        />
      )}
      <Dialog
        open={validatingTestContent && !validationDialog.open}
        fullWidth
        maxWidth="xs"
      >
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              py: 1,
            }}
          >
            <CircularProgress size={24} />
            <Box>
              <Typography variant="body1">Validating test content…</Typography>
              <Typography variant="caption" color="text.secondary">
                Checking for missing data, unsupported features, and other
                common issues.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
      <LlmValidationDialog
        open={validationDialog.open}
        initialMarkdown={validationDialog.markdown}
        initialIssues={validationDialog.issues}
        organisationId={buildData.organisationId}
        platform={sessionConfig.platform}
        onCancel={() =>
          validationDialog.resolve?.({
            choice: "cancel",
            updatedMarkdown: validationDialog.markdown,
            markdownChanged: false,
          })
        }
        onProceed={({ updatedMarkdown, markdownChanged }) =>
          validationDialog.resolve?.({
            choice: "proceed",
            updatedMarkdown,
            markdownChanged,
          })
        }
      />
      <Dialog
        open={preflightDialog.open}
        onClose={() => {
          if (preflightDialog.optimizing) return;
          preflightDialog.resolve?.("cancel");
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Optimizing your test for best results</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your test is missing the Goal and Steps sections. We'll structure
            your content into the canonical Goal / Context / Steps / Success
            Criteria format before running — this helps our agent accurately
            identify real failures and avoid false positives. You can always
            edit the prompt afterwards.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <LoadingButton
            variant="contained"
            loading={preflightDialog.optimizing}
            onClick={() => preflightDialog.resolve?.("optimize")}
          >
            Optimize & Run
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}

function a11yProps(value: string) {
  return {
    id: `simple-tab-${value}`,
    "aria-controls": `simple-tabpanel-${value}`,
    value,
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  value: string;
  hidden: boolean;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, hidden, ...other } = props;

  useEffect(() => {
    if (!hidden) {
      window.dispatchEvent(new Event("resize"));
    }
  }, [hidden]);

  return (
    <div
      role="tabpanel"
      hidden={hidden}
      id={`simple-tabpanel-${value}`}
      aria-labelledby={`simple-tab-${value}`}
      {...other}
    >
      <Box sx={{ p: 1.5 }}>{children}</Box>
    </div>
  );
}

const HeaderWrapper = styled.div`
  display: grid;
  grid-template-areas: "title actions";
  grid-template-columns: minmax(0, 1fr) auto;
  grid-column-gap: 1.5rem;
`;
